import httpClient from '../../httpClient';
import { Listing, ListingEnquiry, ListingStage } from '../../../models/Listing';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { BuyersActivityTrack, Lead, LeadStages, LeadState } from 'models/Lead';
import { Note, NoteTag } from 'models/Note';
import {
  WorkflowConfiguration,
  StepWorkflowConfiguration,
} from 'models/WorkflowConfiguration';
import { NdaStatus } from 'models/Nda';
import {
  DealContractStatus,
  DealOfferStatus,
  DealRoom,
  DealStage,
  ReorderNodes,
} from 'models/DealRoom';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';
import { FilePreview, UploadedFileResponse } from 'models/File';
import { ReactionType, ReactionUsers } from 'models/Reactions';
import { FileReorderViewMode } from 'models/User';

export type LeadsReorderingProps = {
  leadId: string;
  newLeadState: LeadState;
  oldIndex: number;
  oldLeadState: LeadState;
  newIndex: number;
  isManualWaitlisted: boolean;
  tags: string[];
  revokeDataRoomAccess?: boolean;
};

export type ChangeDealStage = {
  stage: DealStage;
  listingStage?: ListingStage;
  offerStatus?: DealOfferStatus;
  contractStatus?: DealContractStatus;
  underOfferLeadId?: string;
  isExternalUnderOfferLead?: boolean;
  settlementDate?: Date;
  settlementPrice?: number;
  lostMandateDescription?: string;
};

function getLeadsForListing(listingId: string, params?: PaginationParams) {
  return httpClient.get(`portfolio/enquiries/${listingId}/leads`, {
    params: {
      $sort: 'index',
      ...params,
    },
  });
}

function updateLead(leadId: string, value: Partial<Lead>) {
  return httpClient.patch<Lead>(`leads/${leadId}`, value);
}

function getTableLeads(
  enquiryId: string,
  params?: PaginationParams & { _id: string },
) {
  return httpClient.get<PaginatedData<Lead>>(
    `portfolio/enquiries/${enquiryId}/leads-dashboard`,
    {
      params,
    },
  );
}

function changeBuyersState(enquiryId: string, values: LeadsReorderingProps) {
  const { leadId, newLeadState, oldLeadState, oldIndex, newIndex } = values;

  return httpClient.post<Lead>(
    `portfolio/leads/enquiries/${enquiryId}/change-buyers-state`,
    { leadId, newLeadState, oldLeadState, oldIndex, newIndex },
  );
}

function addToWaitlist(enquiryId: string, values: LeadsReorderingProps) {
  return httpClient.post<Lead>(
    `portfolio/enquiries/${enquiryId}/add-to-waitlisted`,
    values,
  );
}

function removeFromWaitlist(enquiryId: string, values: LeadsReorderingProps) {
  const { oldLeadState, newLeadState, oldIndex, newIndex, leadId } = values;

  return httpClient.post<Lead>(
    `portfolio/enquiries/${enquiryId}/remove-from-waitlisted`,
    {
      oldLeadState,
      newLeadState,
      oldIndex,
      newIndex,
      leadId,
    },
  );
}

function changeBuyersIndex(leadId: string, values: LeadsReorderingProps) {
  const { oldIndex, newIndex } = values;

  return httpClient.post<Lead>(`portfolio/leads/${leadId}/reorder`, {
    oldIndex,
    newIndex,
  });
}

function pauseListing(listingId: string) {
  return httpClient.post<{
    listing: Listing;
    dealStage: DealStage;
  }>(`portfolio/listings/${listingId}/pause`);
}

function unpauseListing(listingId: string) {
  return httpClient.post<{
    listing: Listing;
    dealStage: DealStage;
  }>(`portfolio/listings/${listingId}/unpause`);
}

function getLeads(listingId: string) {
  return httpClient.get<Array<Partial<Lead>>>(
    `portfolio/enquiries/${listingId}/all-leads`,
  );
}

function saveWorkflowConfiguration(
  listingId: string,
  step: LeadStages,
  newConfig: Partial<WorkflowConfiguration>,
) {
  return httpClient.post<{
    workflowConfigurationStep: WorkflowConfiguration;
    checkedFilesIds: string[];
  }>(`portfolio/workflow-configurations/${listingId}/${step}`, newConfig);
}

function getWorkflowConfiguration(
  enquiryId: string,
  step: LeadStages,
  parentId?: string,
) {
  return httpClient.get<StepWorkflowConfiguration>(
    `portfolio/workflow-configurations/${enquiryId}/${step}`,
    { params: { parentId } },
  );
}

function acceptOffer(leadId: string) {
  return httpClient.post(`portfolio/leads/${leadId}/accept-offer`);
}

function rejectOffer(leadId: string) {
  return httpClient.post(`portfolio/leads/${leadId}/reject-offer`);
}

function linkEnvelopeTemplate(
  listingId: string,
  envelopeTemplateId: string,
  envelopeTemplateName: string,
) {
  return httpClient.post(
    `docusign/listings/${listingId}/link-envelope-template`,
    {
      envelopeTemplateId,
      envelopeTemplateName,
    },
  );
}

function viewNda(leadId: string, envelopeTemplateId: string) {
  return httpClient.get<{ url: string }>(
    `docusign/leads/${leadId}/envelopes/${envelopeTemplateId}/recipient-view-url`,
  );
}

function sendEnvelope(leadId: string, message?: string) {
  return httpClient.post<{ url: string; envelopeId: string }>(
    `docusign/leads/${leadId}/send-envelope`,
    { message },
  );
}

function documentsToUnlockAfterNDA(id: string, documents: string[]) {
  return httpClient.post<Listing>(
    `portfolio/listings/${id}/choose-documents-unlock-sign-nda`,
    { documents },
  );
}

function deleteEnvelopeTemplate(listingId: string) {
  return httpClient.delete<Partial<Listing>>(
    `docusign/listings/${listingId}/envelope-template`,
  );
}

function getLeadNotes(params: PaginationParams, leadId: string) {
  return httpClient.get<PaginatedData<Note>>(`/lead-notes/leads/${leadId}`, {
    params,
  });
}

function archiveLeadNote(noteId: string) {
  return httpClient.post<PaginatedData<Note>>(`lead-notes/${noteId}/archive`);
}

function sendLeadNote(
  leadId: string,
  content: string,
  tags: NoteTag[],
  uploadedFiles: DropzoneFile[],
) {
  return httpClient.post<Note>('lead-notes', {
    content,
    leadId,
    tags,
    uploadedFiles,
  });
}

async function uploadLeadNoteFile(
  leadId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('leadNoteFile', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `lead-notes/leads/${leadId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

const moveFileToDataRoom = (noteId: string, fileId: string) => {
  return httpClient.post(
    `lead-notes/${noteId}/files/${fileId}/move-to-data-room`,
  );
};

const previewNoteFile = (noteId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `lead-notes/${noteId}/files/${fileId}/preview`,
  );
};

const removeNoteFile = (
  noteId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(`lead-notes/${noteId}/files/${fileId}/delete-file`, {
    deleteFromDataRoom,
  });
};

const addNoteReaction = (noteId: string, reactionType: ReactionType) => {
  return httpClient.post<Note>(`lead-notes/${noteId}/reaction`, {
    reactionType,
  });
};

const getNoteReactions = (noteId: string) => {
  return httpClient.get<ReactionUsers>(`lead-notes/${noteId}/reactions`);
};

function setNdaManually(
  leadId: string,
  ndaStatus: NdaStatus,
  uploadedFile?: DropzoneFile,
) {
  return httpClient.post<Lead>(`leads/${leadId}/nda-status`, {
    ndaStatus,
    uploadedFile,
  });
}

function clearDraft(listingId: string) {
  return httpClient.post<Listing>(
    `portfolio/listings/${listingId}/clear-draft`,
  );
}

function snoozeDraft(listingId: string) {
  return httpClient.post<Listing>(
    `portfolio/listings/${listingId}/snooze-draft`,
  );
}

function getDraftListing() {
  return httpClient.get<Listing>(`portfolio/draft-listing`);
}

function changeDealStage(dealId: string, data: ChangeDealStage) {
  return httpClient.post<DealRoom>(`deal-rooms/${dealId}/change-stage`, data);
}

function viewNewLeads(dealId: string, leadIds: string[]) {
  return httpClient.post<DealRoom>(`leads/deal-rooms/${dealId}/broker-view`, {
    leadIds,
  });
}

function getActivities(leadId: string, params: PaginationParams) {
  return httpClient.get<PaginatedData<BuyersActivityTrack>>(
    `leads/${leadId}/buyer-activities`,
    { params },
  );
}

export type PulseCheckValues = ChangeDealStage & { message?: string };

function submitPulseCheck(dealId: string, data: PulseCheckValues) {
  return httpClient.post<DealRoom>(
    `deal-rooms/${dealId}/submit-pulse-check`,
    data,
  );
}

function getFilesSnapshot(leadId: string, params: PaginationParams) {
  return httpClient.get<PaginatedData<DataRoomNode>>(
    `data-room-nodes/leads/${leadId}/files-snapshot`,
    { params },
  );
}

function releaseFileForLead(leadId: string, dataRoomNodeId: string) {
  return httpClient.post<Lead>(`leads/${leadId}/cycle-file-access`, {
    dataRoomNodeId,
  });
}

function undoReleaseFileForLead(leadId: string, dataRoomNodeId: string) {
  return httpClient.post<Lead>(`leads/${leadId}/undo-cycle-file-access`, {
    dataRoomNodeId,
  });
}

function sendPendingFilesReminder(leadId: string, dataRoomNodeIds: string[]) {
  return httpClient.post(`leads/${leadId}/send-pending-files-reminder`, {
    dataRoomNodeIds,
  });
}

function updateView(
  dealRoomId: string,
  body: {
    fileReorderViewMode: FileReorderViewMode;
    dataRoomTopNodes?: ReorderNodes[];
    dataRoomStarredNodes?: ReorderNodes[];
  },
) {
  return httpClient.post<DealRoom>(
    `deal-rooms/${dealRoomId}/update-view`,
    body,
  );
}

async function uploadNDAProof(
  file: File,
  leadId: string,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('ndaFile', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `portfolio/leads/${leadId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

const moveNdaProofFileToDataRoom = (leadId: string) => {
  return httpClient.post(
    `portfolio/leads/${leadId}/move-nda-file-to-data-room`,
  );
};

const previewNdaProofFile = (leadId: string) => {
  return httpClient.get<FilePreview>(
    `portfolio/leads/${leadId}/preview-nda-file`,
  );
};

const removeNdaProofFile = (
  leadId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `portfolio/leads/${leadId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

const addPreviousLead = (
  listingId: string,
  data: { buyerIds: string[]; customMessage?: string },
) => {
  return httpClient.post<ListingEnquiry>(
    `portfolio/listings/${listingId}/add-previous-leads`,
    data,
  );
};

function searchDeals(dealRoomId: string, searchString: string) {
  return httpClient.get<Partial<DealRoom>[]>(
    `deal-rooms/${dealRoomId}/search-all`,
    {
      params: { $searchString: searchString },
    },
  );
}

function getLead(leadId: string) {
  return httpClient.get<Lead>(`leads/${leadId}`);
}

export default {
  getLeadsForListing,
  changeBuyersState,
  changeBuyersIndex,
  pauseListing,
  unpauseListing,
  getLeads,
  addToWaitlist,
  saveWorkflowConfiguration,
  getWorkflowConfiguration,
  acceptOffer,
  rejectOffer,
  linkEnvelopeTemplate,
  viewNda,
  sendEnvelope,
  documentsToUnlockAfterNDA,
  deleteEnvelopeTemplate,
  getTableLeads,
  getLeadNotes,
  archiveLeadNote,
  sendLeadNote,
  updateLead,
  setNdaManually,
  clearDraft,
  getDraftListing,
  changeDealStage,
  removeFromWaitlist,
  viewNewLeads,
  getActivities,
  snoozeDraft,
  submitPulseCheck,
  getFilesSnapshot,
  releaseFileForLead,
  sendPendingFilesReminder,
  updateView,
  uploadLeadNoteFile,
  moveFileToDataRoom,
  removeNoteFile,
  previewNoteFile,
  uploadNDAProof,
  moveNdaProofFileToDataRoom,
  removeNdaProofFile,
  previewNdaProofFile,
  addNoteReaction,
  getNoteReactions,
  addPreviousLead,
  searchDeals,
  undoReleaseFileForLead,
  getLead,
};
